import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Heart, Instagram, Youtube, Sparkles, Apple, Sun, Users, Mail, Phone } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  return (
    <footer className="bg-gray-900 text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-8 mb-8">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <Heart className="h-8 w-8 text-[#FFB3AB]" />
              <span className="text-2xl font-bold">Pilatika</span>
            </div>
            <p className="text-gray-400 mb-2 font-light italic">
              Move • Flow • Evolve
            </p>
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              {isEnglish 
                ? 'Turkey\'s leading online wellness platform. AI-powered coaching, personalized diet plans, and expert-led pilates, yoga classes.'
                : 'Türkiye\'nin lider online wellness platformu. Yapay zeka destekli koçluk, kişiselleştirilmiş diyet planları ve uzman eğitmenlerle pilates, yoga dersleri.'}
            </p>
            <div className="flex space-x-4 mb-4">
              <a href="https://instagram.com/pilatikacom" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors" aria-label="Instagram" data-testid="social-instagram">
                <Instagram className="h-6 w-6" />
              </a>
              <a href="https://youtube.com/@pilatika" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors" aria-label="YouTube" data-testid="social-youtube">
                <Youtube className="h-6 w-6" />
              </a>
              <a href="https://tiktok.com/@pilatikacom" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors" aria-label="TikTok" data-testid="social-tiktok">
                <FaTiktok className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* AI Features */}
          <div>
            <h3 className="font-semibold mb-4 text-white">{isEnglish ? 'AI Features' : 'AI Özellikler'}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/ai-coach" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  AI Koç
                </Link>
              </li>
              <li>
                <Link to="/ai-dietitian" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Apple className="h-4 w-4" />
                  AI Diyetisyen
                </Link>
              </li>
              <li>
                <Link to="/daily-checkin" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Sun className="h-4 w-4" />
                  Günlük Check-in
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'Pricing' : 'Fiyatlar'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white">{isEnglish ? 'Explore' : 'Keşfet'}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/lessons" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'Online Classes' : 'Online Dersler'}
                </Link>
              </li>
              <li>
                <Link to="/instructors" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'Instructors' : 'Eğitmenler'}
                </Link>
              </li>
              <li>
                <Link to="/become-instructor" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {isEnglish ? 'Become Instructor' : 'Eğitmen Ol'}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'About Us' : 'Hakkımızda'}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'Contact' : 'İletişim'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold mb-4 text-white">{isEnglish ? 'Legal' : 'Yasal'}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'Privacy Policy' : 'Gizlilik Politikası'}
                </Link>
              </li>
              <li>
                <Link to="/kvkk" className="text-gray-400 hover:text-white transition-colors">
                  KVKK
                </Link>
              </li>
              <li>
                <Link to="/refund" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'Refund Policy' : 'İade Politikası'}
                </Link>
              </li>
              <li>
                <Link to="/register" className="text-gray-400 hover:text-white transition-colors">
                  {isEnglish ? 'Sign Up' : 'Üye Ol'}
                </Link>
              </li>
            </ul>
            
            {/* Contact Info */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <a href="mailto:info@pilatika.com" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4" />
                info@pilatika.com
              </a>
            </div>
          </div>
        </div>

        {/* iyzico Badge */}
        <div className="border-t border-gray-800 pt-6 mb-6">
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-4">
            <img 
              src="/iyzico-logo-pack/footer_iyzico_ile_ode/White/logo_band_white@2x.png" 
              alt="iyzico ile güvenli ödeme" 
              className="h-8"
              width="200"
              height="32"
              loading="lazy"
            />
            <p className="text-gray-400 text-sm">
              {isEnglish 
                ? 'All payments are secured with iyzico.' 
                : 'Ödemeler iyzico güvencesiyle alınmaktadır.'}
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-6 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} Pilatika. {isEnglish ? 'All rights reserved.' : 'Tüm hakları saklıdır.'}</p>
        </div>
      </div>
    </footer>
  );
};
